import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';

interface Receipt {
  receiptId: string;
  orderNumber: string;
  orderDate: number;
  dateFormatted: string;
  store: string;
  lineItemCount: number;
  totalNetFormatted: string;
}

interface ReceiptsResponse {
  receipts: Receipt[];
  count: number;
}

export default function ReceiptsPage() {
  const navigate = useNavigate();

  const [data, setData] = useState<ReceiptsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.get(`/api/receipts`);
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch receipts:', err);
        setError('Failed to load receipts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipts();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading receipts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
        <h2 className="text-lg font-bold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold text-ink-700">Orders</h1>
        <p className="text-sm text-ink-500 max-w-2xl">
          Every confirmed grocery receipt you forward appears here with quick access to totals, item counts, and full details.
        </p>
      </div>

      {data && data.count > 0 ? (
        <div className="table-card">
          <table className="table-default">
            <thead>
              <tr>
                <th>Date</th>
                <th>Order #</th>
                <th>Store</th>
                <th className="text-center">Items</th>
                <th className="text-right">Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/80">
              {data.receipts.map((receipt) => (
                <tr key={receipt.receiptId}>
                  <td className="text-ink-600">{receipt.dateFormatted}</td>
                  <td className="font-semibold text-ink-700">{receipt.orderNumber}</td>
                  <td className="text-ink-500">
                    {receipt.store.charAt(0).toUpperCase() + receipt.store.slice(1)}
                  </td>
                  <td className="text-center text-ink-500">{receipt.lineItemCount}</td>
                  <td className="text-right font-semibold text-ink-700">{receipt.totalNetFormatted}</td>
                  <td>
                    <button
                      onClick={() => navigate(`/orders/${receipt.receiptId}`)}
                      className="btn-secondary"
                    >
                      View details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card border border-dashed border-ink-200/80 bg-white/70 p-8 text-sm text-ink-500">
          No receipts yet. Forward your Sainsbury's order confirmations to kick-start your analytics.
        </div>
      )}
    </div>
  );
}
